import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Box from '@mui/material/Box'
import MapToolRotation from './MapToolRotation'
import MapToolCurrentLocation from './MapToolCurrentLocation'
import BaseMapToggle from './MapToolBaseMapToggle'
import MapResultMobile from './MapResultMobile'
import MapToolSpeedDial from './MapToolSpeedDial'
import Paper from '@mui/material/Paper'
import useMapStore from '../../stores/useMapStore'
import { observer } from 'mobx-react-lite'
import { ReactComponent as SignalGoodIcon } from '../../icon/signal_good.svg'
import { ReactComponent as SignalModerateIcon } from '../../icon/signal_moderate.svg'
import { ReactComponent as SignalBadIcon } from '../../icon/signal_bad.svg'
import { ReactComponent as SignalNormalIcon } from '../../icon/signal_normal.svg'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { probabilityRatio } from '../../constant/Constant'
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow'

const windowHeightMobile = 510

type MapResultToggleMobileProps = {
    data?: SearchDataType | null
    disabledCurrentLocation?: boolean
    expanded: boolean
    onClickResult: (item: StationType) => void
    onClickShowExpand: () => void
    onClickHideExpand: () => void
    onBaseMapSelected?: (name?: string) => void
    onClickRotation: () => void
    onClickCurrentLocation: () => void
    resetSearchData: () => void
}
const MapResultToggleMobile = observer((props: MapResultToggleMobileProps) => {
    const {
        data,
        disabledCurrentLocation,
        expanded,
        onClickResult,
        onClickShowExpand,
        onClickHideExpand,
        onBaseMapSelected,
        onClickRotation,
        onClickCurrentLocation,
        resetSearchData,
    } = props

    useEffect(() => {
        const handleResize = () => {
            // if (openBasemapGallery){
            //     setOpenBasemapGallery(false)
            // }
            setOpenBasemapGallery(false)
            setWindowDimensions(getWindowDimensions())
        }

        globalThis.addEventListener('resize', handleResize)
        return () => globalThis.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        setOpenBasemapGallery(false)
        if (data && data.stations?.length) {
            onClickShowExpand()
        } else {
            onClickHideExpand()
        }
    }, [data])

    useEffect(() => {
        setShowResult(expanded)
    }, [expanded])

    const onClickBaseMap = () => {
        setOpenBasemapGallery((prev) => !prev)
    }

    const onClickSpeedDial = (isOpen: boolean) => {
        setOpenSpeedDial(isOpen)
    }

    const closeBasemapGallery = (flag: boolean) => {
        setOpenBasemapGallery(flag)
    }

    const onHilightSelectStation = (data: number) => {
        mapStore.hilightSelectStation(data)
    }

    const getSignalStrength = (signalStrength?: number) => {
        if (signalStrength)
            if (signalStrength > probabilityRatio.good)
                return { icon: <SignalGoodIcon />, message: `คาดว่า${t('LABEL.MAP.MAP_RESULT.GOOD_RECEPTION')}ได้ดี` }
            else if (signalStrength > probabilityRatio.average)
                return { icon: <SignalModerateIcon />, message: `คาดว่า${t('LABEL.MAP.MAP_RESULT.GOOD_RECEPTION')}ได้` }
            else if (signalStrength > probabilityRatio.bad)
                return { icon: <SignalBadIcon />, message: `คาดว่าพอจะ${t('LABEL.MAP.MAP_RESULT.GOOD_RECEPTION')}ได้` }
        return { icon: <SignalNormalIcon style={{ fill: '#333333' }} />, message: '' }
    }

    const getWindowDimensions = () => {
        const { innerWidth: width, innerHeight: height } = window
        return { width, height }
    }

    const [showResult, setShowResult] = useState(false)
    const [openSpeedDial, setOpenSpeedDial] = useState(false)
    const [openBasemapGallery, setOpenBasemapGallery] = useState(false)
    const [openTooltipSignalStrength, setOpenTooltipSignalStrength] = useState(false)
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

    const { t } = useTranslation()
    const mapStore = useMapStore()
    const classes = useStyles()

    const onClickTooltipSignalStrength = () => setOpenTooltipSignalStrength(!openTooltipSignalStrength)

    return (
        <div className={clsx(classes.slide, { [classes.slideIn]: showResult, [classes.slideOut]: !showResult })}>
            <div className={classes.elementTools}>
                <BaseMapToggle
                    onBaseMapSelected={onBaseMapSelected}
                    openBasemapGallery={openBasemapGallery}
                    onClickBaseMap={onClickBaseMap}
                    closeBasemapGallery={closeBasemapGallery}
                    positionTop={140}
                />

                {data?.stations && data.stations.length > 0 && (
                    <Tooltip
                        open={openTooltipSignalStrength}
                        title={getSignalStrength(data?.probability).message}
                        placement="right"
                        onClick={onClickTooltipSignalStrength}
                        onMouseOver={() => setOpenTooltipSignalStrength(true)}
                        onMouseLeave={() => setOpenTooltipSignalStrength(false)}
                        classes={{ tooltip: classes.tooltipSignalStrength }}
                    >
                        <Paper className={classes.reception}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                {getSignalStrength(data?.probability).icon}
                            </Stack>
                        </Paper>
                    </Tooltip>
                )}

                <Box className={classes.mapTool}>
                    {windowDimensions.height >= windowHeightMobile ? (
                        <>
                            <MapToolRotation onClickRotation={onClickRotation} />
                            <MapToolCurrentLocation
                                disabled={disabledCurrentLocation}
                                onClickCurrentLocation={onClickCurrentLocation}
                            />
                        </>
                    ) : (
                        <MapToolSpeedDial
                            platform={'mobile'}
                            isOpen={openSpeedDial}
                            disabledCurrentLocation={disabledCurrentLocation}
                            onClickCurrentLocation={onClickCurrentLocation}
                            onClickSpeedDial={onClickSpeedDial}
                            onClickRotation={onClickRotation}
                        />
                    )}
                </Box>

                {data?.stations && data.stations.length > 0 && !openSpeedDial && (
                    <>
                        {showResult ? (
                            <Paper
                                className={
                                    windowDimensions.height >= windowHeightMobile
                                        ? classes.buttonHideResult
                                        : classes.buttonHideResultMobile
                                }
                                component="div"
                                onClick={() => {
                                    showResult ? onClickHideExpand() : onClickShowExpand()
                                    setOpenBasemapGallery(false)
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                    <Typography className={classes.textHideResult}>
                                        {t('BUTTON.HIDE_RESULT')}
                                    </Typography>
                                    <DoubleArrowIcon style={{ transform: 'rotate(90deg)', fontSize: '18px' }} />
                                </Stack>
                            </Paper>
                        ) : (
                            <Paper
                                className={
                                    windowDimensions.height >= windowHeightMobile
                                        ? classes.buttonShowResult
                                        : classes.buttonShowResultMobile
                                }
                                component="div"
                                onClick={() => {
                                    showResult ? onClickHideExpand() : onClickShowExpand()
                                    setOpenBasemapGallery(false)
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                    <Typography className={classes.textShowResult}>
                                        {t('BUTTON.SHOW_RESULT')}
                                    </Typography>
                                    <DoubleArrowIcon
                                        style={{ transform: 'rotate(-90deg)', color: 'white', fontSize: '18px' }}
                                    />
                                </Stack>
                            </Paper>
                        )}
                        <Paper
                            className={
                                windowDimensions.height >= windowHeightMobile
                                    ? classes.btnClose
                                    : classes.btnCloseMobile
                            }
                            component="div"
                        >
                            <Tooltip title={t('BUTTON.CLOSE') ?? ''} placement="bottom">
                                <IconButton aria-label="close" onClick={() => resetSearchData()}>
                                    <CloseIcon sx={{ color: '#fff' }} />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    </>
                )}
            </div>
            <div className={classes.elementResult}>
                <MapResultMobile
                    data={data}
                    stationNumber={mapStore.selectStationNumber}
                    onClickResult={onClickResult}
                    onHilightSelectStation={onHilightSelectStation}
                    resetSearchData={resetSearchData}
                />
            </div>
        </div>
    )
})

export default MapResultToggleMobile

const useStyles = makeStyles((theme: Theme) => ({
    slide: {
        position: 'fixed',
        width: '100%',
        height: 'auto',
    },
    slideIn: {
        animation: '$slideIn 0.5s forwards',
    },
    slideOut: {
        animation: '$slideOut 0.1s forwards',
    },
    '@keyframes slideIn': {
        '0%': {
            transform: 'translateY(0%)',
        },
        '100%': {
            transform: 'translateY(-50%)',
        },
    },
    '@keyframes slideOut': {
        '100%': {
            transform: 'translateY(0%)',
        },
    },
    elementTools: {
        display: 'flex',
        justifyContent: 'end',
    },
    elementResult: {
        display: 'inline-block',
        width: '100%',
        height: '310px',
        backgroundColor: 'rgba(255, 255, 255, 0)',
    },
    mapTool: {
        position: 'absolute',
        right: '16px',
        top: '-156px',
    },
    textResult: {
        fontSize: '15px !important',
        lineHeight: '23px !important',
        paddingTop: '12px',
        paddingBottom: '9.72px',
    },
    reception: {
        padding: theme.spacing(1),
        backgroundColor: '#fff',
        position: 'absolute',
        left: '16px',
        bottom: '320px',
        borderRadius: '5px',
    },
    textHideResult: {
        color: '#333333',
        fontSize: '14px !important',
    },
    textShowResult: {
        color: '#fff',
        fontSize: '14px !important',
    },
    buttonHideResult: {
        padding: theme.spacing(1),
        backgroundColor: '#fff !important',
        position: 'absolute',
        right: '60px',
        bottom: '320px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    buttonHideResultMobile: {
        padding: theme.spacing(1),
        backgroundColor: '#fff !important',
        position: 'absolute',
        right: '115px',
        bottom: '320px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    buttonShowResult: {
        padding: theme.spacing(1),
        backgroundColor: `${theme.palette.primary.main} !important`,
        position: 'absolute',
        right: '60px',
        bottom: '320px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    buttonShowResultMobile: {
        padding: theme.spacing(1),
        backgroundColor: `${theme.palette.primary.main} !important`,
        position: 'absolute',
        right: '115px',
        bottom: '320px',
        borderRadius: '5px',
        cursor: 'pointer',
    },
    btnClose: {
        backgroundColor: 'red !important',
        position: 'absolute',
        right: `19px !important`,
        bottom: '320px',
        borderRadius: '50% !important',
    },
    btnCloseMobile: {
        backgroundColor: 'red !important',
        position: 'absolute',
        right: `71px !important`,
        bottom: '320px',
        borderRadius: '50% !important',
    },
    tooltipSignalStrength: {
        margin: '5px !important',
        fontSize: '14px !important',
    },
}))
